import appSettings from '../appsettings.json';
import { Artist, Genre, Song } from './library-objects';
import { Instrument, PartList } from './music-objects';
import { Project, ProjectPublication, ProjectSubmission } from './project-objects';
import { Classroom, Location, ProjectSubmissionToken, User } from './user-objects';
import { Utils } from './utils';

// API object definitions
export interface AccessCodeValidationInfo {
	isValid: boolean;
	accessCode: string;
	classroomId: string;
	classroomName: string;
	isStudentAccessCode: boolean;
	isClassroomAdminAccessCode: boolean;
	isConductionAdminAccessCode: boolean;
	isLimitReached: boolean;
}

export interface ActionResponse {
	success: boolean;
	message: string;
	errorDetails?: string;
}

export interface FileResponse {
	fileBlobUrl: string;
	success: boolean;
	message: string;
}
interface ValidationErrorResponse {
	message: string;
	error: string;
}

export interface SongForm {
	songName: string;
	genreIds: string[];
	artistIds: string[];
}

export interface ClassroomForm {
	name: string,
	studentLimit: number,
	classroomAdminLimit: number,
	defaultInstrumentIds: string[],
	defaultSongIds: string[]
}

export interface SubmissionTokenForm {
	credits: number;
	start: Date;
	expiration: Date;
}

export interface ArtistStats {
	artistId: string;
	artistName: string;
	artistType: string;
	userCount: number;
	classroomCount: number;
	songs: Song[];
}

export interface BalancingResult {
	success: boolean | null,
	message: string
};

export interface PagedResults<T> {
	items: T[];
	page: number;
	pageSize: number;
	totalCount: number;
	totalPages: number;
	hasPrevious: boolean;
	hasNext: boolean;
}

export interface BulkRegisterStudentItem {
	username?: string;
	password: string;
	email?: string;
	firstName?: string;
	lastName?: string;
}

export interface BulkRegisterStudentResult {
	index: number;           // position in the submitted array
	userId: string;
	username: string;
	email?: string | null;
}

export interface CreateMultipartResponseDto {
	key: string;
	uploadId: string;
	partSizeBytes: number;
}

export interface CompletedPart {
	partNumber: number;
	eTag: string;
}

export interface SignedPartUrl {
	partNumber: number;
	url: string;
}

export interface CurrentTos {
	version: string;
	sha256: string;
}

interface ApiResponse {
	errorMessage?: string;
	errorDetails?: string;
	response?: Response | null;
}

export enum ActivityFeedItemKind {
	Submission = 0,
	Publication = 1,
}

export type ActivityFeedItem =
	| {
		kind: ActivityFeedItemKind.Submission;
		eventDate: string | null; // ISO string from server
		submission: ProjectSubmission;
		publication?: undefined;
	}
	| {
		kind: ActivityFeedItemKind.Publication;
		eventDate: string | null;
		submission?: undefined;
		publication: ProjectPublication;
	};


export default class ConductionAPI {

	private static async callEndpoint(url: string, method: string, body: any, token?: string): Promise<ApiResponse> {
		try {
			const bodyString = body ? JSON.stringify(body) : undefined;
			const response = await fetch(url, {
				method: method,
				headers: token ? this.getAuthHeaders(token) : { 'Content-Type': 'application/json' },
				body: bodyString
			});

			// If the response is not ok, handle it gracefully
			if (!response.ok) {
				// Log the error details for debugging
				console.error(`Error occurred during ${method} request to ${url}:`, response.status, response.statusText);
				// Return the error without throwing
				return {
					errorMessage: "An error occurred while processing the request.",
					errorDetails: `Error: ${response.status} ${response.statusText}`,
					response: response
				};
			}

			// If the response is ok, return it
			return {
				response: response
			};

		} catch (error) {
			// Fetch throws a TypeError on network failure (e.g. DNS, timeout, blocked)
			if (error instanceof TypeError) {
				// Handle network error
				console.error("Network error:", error.message);

				// Handle network errors or other unexpected errors
				return {
					errorMessage: "A network error occurred. Please check your connection or try again later.",
					errorDetails: `Network error details: ${error.message}`
				};
			}
			// Handle other errors
			console.error("An unexpected error occurred:", error);
			return {
				errorMessage: "An unexpected error occurred.",
			};
		}
	}

	private static getAuthHeaders(token: string, options?: { excludeContentType?: boolean }): Headers {
		if (token) {
			const headers = new Headers();
			headers.append('Authorization', `Bearer ${token}`);
			if (!options?.excludeContentType) {
				headers.append('Content-Type', 'application/json');
			}
			return headers;
		}
		throw Error('Not logged in');
	}

	private static getGuestAuthHeaders(guestToken: string): Headers {
		if (guestToken) {
			const headers = new Headers();
			headers.append('Authorization', `Bearer ${guestToken}`);
			headers.append('Content-Type', 'application/json');
			return headers;
		}
		throw Error('Not in guest mode');
	}

	public static async updateUsername(user: User, newUsername: string, token: string): Promise<void> {
		const url = `${appSettings.ConductionApiBaseUrl}users/${user.id}/username`;

		const endpointCall = await ConductionAPI.callEndpoint(url, 'PUT', newUsername, token);

		if (endpointCall.errorMessage !== undefined) {
			throw new Error("An error occurred while updating the username.");
		}
	}

	public static async updatePassword(user: User, newPassword: string, token: string): Promise<void> {
		const url = `${appSettings.ConductionApiBaseUrl}users/${user.id}/password`;

		const endpointCall = await ConductionAPI.callEndpoint(url, 'PUT', newPassword, token);

		if (endpointCall.errorMessage !== undefined) {
			throw new Error("An error occurred while updating the password.");
		}
	}

	public static async getAllSongs(token: string): Promise<Song[] | null> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}all-songs`);
			const response = await fetch(url, {
				method: 'get',
				headers: ConductionAPI.getAuthHeaders(token)
			});

			if (!response.ok) {
				console.error('Error retrieving songs:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((song: any) => Song.fromJson(song));
		} catch (error) {
			console.error('An error occurred while retrieving songs:', error);
			return null;
		}
	}

	public static async getArtistStats(artistId: string): Promise<ArtistStats> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}artists/${artistId}/stats`);

			const response = await fetch(url.toString(), {
				method: 'GET',
			});

			if (!response.ok) {
				console.error('Error retrieving artist stats:', response.status, response.statusText);
				throw new Error(`Error retrieving artist stats: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();
			return {
				artistId: data.artistId,
				artistName: data.artistName,
				artistType: data.artistType,
				userCount: data.userCount,
				classroomCount: data.classroomCount,
				songs: data.songs.map((song: any) => Song.fromJson(song))
			};
		} catch (error) {
			console.error('An error occurred while retrieving artist stats:', error);
			throw error;
		}
	}

	public static async getArtists(): Promise<Artist[] | null> {
		try {
			const response = await fetch(`${appSettings.ConductionApiBaseUrl}artists`);

			if (!response.ok) {
				console.error('Error retrieving artists:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((artist: any) => Artist.fromJson(artist));
		} catch (error) {
			console.error('An error occurred while retrieving genres:', error);
			return null;
		}
	}

	public static async updateArtistLocation(artistId: string, locationId: string, token: string): Promise<void> {
		const url = new URL(
			`artists/${encodeURIComponent(artistId)}/location/${encodeURIComponent(locationId)}`,
			appSettings.ConductionApiBaseUrl
		);

		const resp = await fetch(url.toString(), {
			method: "PUT",
			headers: ConductionAPI.getAuthHeaders(token),
		});
		if (!resp.ok) {
			let message = `(${resp.status}) ${resp.statusText}`;
			try {
				const j = await resp.json();
				if (typeof j?.error === "string") message = j.error;
				else if (typeof j?.message === "string") message = j.message;
			} catch { /* ignore */ }
			throw new Error(`Error updating artist location: ${message}`);
		}
	}

	public static async getGenres(): Promise<Genre[] | null> {
		try {
			const response = await fetch(`${appSettings.ConductionApiBaseUrl}genres`);

			if (!response.ok) {
				console.error('Error retrieving genres:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((genre: any) => Genre.fromJson(genre));
		} catch (error) {
			console.error('An error occurred while retrieving genres:', error);
			return null;
		}
	}

	public static async getInstruments(): Promise<Instrument[] | null> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}instruments`);

			const response = await fetch(url, {
				method: 'get',
				//headers: ConductionAPI.getAuthHeaders()
			});

			if (!response.ok) {
				console.error('Error retrieving instruments:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((instrument: any) => Instrument.fromJson(instrument));
		} catch (error) {
			console.error('An error occurred while retrieving instruments:', error);
			return null;
		}
	}

	public static async getCurrentTos(): Promise<CurrentTos | null> {
		try {
			const url = new URL(`tos/current`, appSettings.PerformanceApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' }
			});

			if (!response.ok) {
				// 500 when no current TOS is set; just return null so caller can decide UX.
				console.error('Error retrieving current TOS:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return { version: data.version, sha256: data.sha256 } as CurrentTos;
		} catch (error) {
			console.error('An error occurred while retrieving current TOS:', error);
			return null;
		}
	}

	public static async acceptTos(version: string, token: string, locale?: string): Promise<void> {
		const url = new URL(`tos/accept`, appSettings.PerformanceApiBaseUrl);
		const body = { version, locale };

		const response = await fetch(url.toString(), {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(body)
		});

		if (!response.ok) {
			// Try to surface server message (handles 400 version mismatch, 401 unauthorized, etc.)
			let message = `(${response.status}) ${response.statusText}`;
			try {
				const j = await response.json();
				if (Array.isArray(j?.errors) && j.errors.length) {
					message = j.errors.join(' ');
				} else if (typeof j?.error === 'string') {
					message = j.error;
				} else if (typeof j?.message === 'string') {
					message = j.message;
				}
			} catch {
				// ignore JSON parse error; keep default message
			}
			throw new Error(`Error accepting Terms of Service: ${message}`);
		}
	}

	public static async getAllUsers(token: string): Promise<User[] | null> {
		try {
			const url = new URL("users", appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving users:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((user: any) => {
				return User.fromJson(user);
			});
		} catch (error) {
			console.error('An error occurred while retrieving users:', error);
			return null;
		}
	}

	public static async getUser(userId: string, token: string): Promise<User | null> {
		try {
			const url = new URL(`users/${userId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url, {
				method: 'get',
				headers: ConductionAPI.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving user data:', response.status, response.statusText);
				return null;
			}

			const userData = await response.json();
			return User.fromJson(userData);
		} catch (error) {
			console.error('An error occurred while retrieving user data:', error);
			return null;
		}
	}

	public static async getUserSongs(token: string): Promise<Song[] | null> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}usersongs`);

			const response = await fetch(url.toString(), {
				method: 'get',
				headers: ConductionAPI.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving songs:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((songData: any) => Song.fromJson(songData));
		} catch (error) {
			console.error('An error occurred while retrieving songs:', error);
			return null;
		}
	}

	public static async getMarketplaceSongs(): Promise<Song[] | null> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}marketplace-songs`);

			const response = await fetch(url.toString(), {
				method: 'get'
			});

			if (!response.ok) {
				console.error('Error retrieving songs:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((songData: any) => Song.fromJson(songData));
		} catch (error) {
			console.error('An error occurred while retrieving songs:', error);
			return null;
		}
	}

	public static async getSongMusicXml(songId: string): Promise<string> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}songs/${songId}/musicxml`);

			const response = await fetch(url.toString(), {
				method: 'get'
			});

			if (!response.ok) {
				console.error('Error retrieving musicxml:', response.status, response.statusText);
				throw new Error(`Error retrieving musicxml: ${response.status} ${response.statusText}`);
			}

			const musicXmlString = await response.text();
			if (!musicXmlString) {
				throw new Error('MusicXML is empty or not found.');
			}
			return musicXmlString;
		} catch (error) {
			console.error('An error occurred while retrieving musicxml:', error);
			throw error;
		}
	}

	public static async getGuestSongs(token: string): Promise<Song[] | null> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}songs`);

			const response = await fetch(url.toString(), {
				method: 'get',
				headers: ConductionAPI.getGuestAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving songs:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((songData: any) => Song.fromJson(songData));
		} catch (error) {
			console.error('An error occurred while retrieving songs:', error);
			throw error;
		}
	}

	public static async getUnprocessedSongData(songId: string, token: string): Promise<PartList | string> {
		const url = new URL(`${appSettings.PerformanceApiBaseUrl}songs/${songId}/unprocessed`);

		const response = await fetch(url, {
			method: 'get',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			const errorData = (await response.json()) as ValidationErrorResponse;
			return errorData.error || "Unknown error occurred.";
		}

		try {
			const data = await response.json();
			return PartList.fromJson(data);
		} catch (error) {
			console.error("Error parsing response JSON:", error);
			return "Failed to parse song data.";
		}
	}

	public static async getSong(songId: string, token: string): Promise<Song | null> {
		try {
			const url = new URL(`songs/${songId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving song info:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return Song.fromJson(data);
		} catch (error) {
			console.error('An error occurred while retrieving song info:', error);
			return null;
		}
	}

	public static async getSongData(songId: string): Promise<PartList | null> {
		try {
			const response = await fetch(`${appSettings.ConductionApiBaseUrl}songs/${songId}/data`);

			if (!response.ok) {
				console.error('Error retrieving song data:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			const partList = PartList.fromJson(data);
			partList.setSongId(songId); // This is an important reference when creating timeline items so we can track song usage.
			return partList;
		} catch (error) {
			console.error('An error occurred while retrieving song data:', error);
			return null;
		}
	}

	public static async deleteSong(songId: string, token: string): Promise<void> {
		try {
			const url = new URL(`songs/${songId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'DELETE',
				headers: ConductionAPI.getAuthHeaders(token)
			});

			if (!response.ok) {
				throw new Error(`Error occurred during deletion: ${response.status} ${response.statusText}`);
			}
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			throw new Error(`An error occurred while deleting song: ${errorMessage}`);
		}
	}

	public static async uploadMusicXML(songId: string, file: File, token: string): Promise<void> {
		try {
			const url = new URL(`songs/${songId}/musicxml`, appSettings.PerformanceApiBaseUrl);

			const formData = new FormData();
			formData.append('musicXmlFile', file); // Ensure the field name matches what the server expects

			const response = await fetch(url.toString(), {
				method: 'PUT',
				headers: ConductionAPI.getAuthHeaders(token, { excludeContentType: true }), // Exclude Content-Type
				body: formData,
			});

			if (!response.ok) {
				throw new Error(`Errors found: ${response.status} ${response.statusText}`);
			}
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			throw new Error(`An error occurred while uploading musicxml: ${errorMessage}`);
		}
	}

	public static async createNewSongEntry(songForm: SongForm, token: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}songs`;

		const songFormString = JSON.stringify(songForm);

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: songFormString
		});

		if (!response.ok) {
			throw new Error(`Error adding song: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async logSearchTerm(searchTerm: string, token: string): Promise<void> {
		try {
			const url = new URL(`search-term/${searchTerm}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'POST',
				headers: ConductionAPI.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error sending search terms:', response.status, response.statusText);
			}

			return;
		} catch (error) {
			console.error('An error occurred while searching for songs:', error);
			return;
		}
	}

	public static async processSongData(songId: string, token: string): Promise<void> {
		try {
			const url = new URL(`songs/${songId}/process`, appSettings.PerformanceApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'PUT',
				headers: ConductionAPI.getAuthHeaders(token)
			});

			if (!response.ok) {
				throw new Error(`Errors: ${response.status} ${response.statusText}`);
			}
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			throw new Error(`An error occurred while uploading musicxml: ${errorMessage}`);
		}
	}

	public static async setSongGuestAvailability(songId: string, newAvailabilityStatus: boolean, token: string) {
		const url = new URL(`songs/${songId}/guests`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: newAvailabilityStatus ? 'PUT' : 'DELETE',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error setting guest availibility: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async updateSongAssignment(songId: string, classroomIds: string[], token: string) {
		const url = new URL(`songs/${songId}/classroom-assignments`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(classroomIds)
		});

		if (!response.ok) {
			throw new Error(`Error assigning song: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async updateClassroomSongAssignments(classroomId: string, songIds: string[], token: string) {
		const url = new URL(`classrooms/${classroomId}/default-songs`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(songIds)
		});

		if (!response.ok) {
			throw new Error(`Error assigning songs to classroom: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async updateClassroomLocation(classroomId: string, locationId: string, token: string): Promise<void> {
		const url = new URL(
			`classrooms/${encodeURIComponent(classroomId)}/location/${encodeURIComponent(locationId)}`,
			appSettings.ConductionApiBaseUrl
		);

		const resp = await fetch(url.toString(), {
			method: "PUT",
			headers: ConductionAPI.getAuthHeaders(token),
		});
		if (!resp.ok) {
			let message = `(${resp.status}) ${resp.statusText}`;
			try {
				const j = await resp.json();
				if (typeof j?.error === "string") message = j.error;
				else if (typeof j?.message === "string") message = j.message;
			} catch { /* ignore */ }
			throw new Error(`Error updating classroom location: ${message}`);
		}
	}

	public static async getProjects(userId: string, token: string): Promise<Project[] | null> {
		try {
			if (userId == undefined) {
				throw new Error('User ID is not defined');
			}
			const url = new URL(`users/${userId}/projects`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving projects:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((project: any) => Project.fromJson(project));
		} catch (error) {
			console.error('An error occurred while retrieving projects:', error);
			return null;
		}
	}

	public static async createProject(project: Project, token: string): Promise<string | null> {
		try {
			const url = new URL("projects", appSettings.ConductionApiBaseUrl);
			const projectJson = project.toJson();
			const response = await fetch(url, {
				method: 'POST',
				headers: ConductionAPI.getAuthHeaders(token),
				body: JSON.stringify(projectJson)
			});

			if (!response.ok) {
				console.error('Error saving project:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.id;
		} catch (error) {
			console.error('An error occurred while saving the project:', error);
			return null;
		}
	}

	public static async saveProject(project: Project, token: string): Promise<boolean> {
		try {
			const url = `${appSettings.ConductionApiBaseUrl}projects`;
			const projectJson = project.toJson();
			const response = await fetch(url, {
				method: 'PUT',
				headers: ConductionAPI.getAuthHeaders(token),
				body: JSON.stringify(projectJson)
			});

			if (!response.ok) {
				console.error('Error saving project:', response.status, response.statusText);
				return false;
			}
			return true;
		} catch (error) {
			console.error('An error occurred while saving the project:', error);
			return false;
		}
	}

	public static async loadProject(projectId: string, token: string): Promise<Project> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}projects/load/${projectId}`);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving project:', response.status, response.statusText);
			}

			const data = await response.json();
			return Project.fromJson(data);
		} catch (error) {
			throw error;
		}
	}

	public static async getClassroomUsers(classroomId: string, token: string): Promise<User[] | null> {
		try {
			const url = new URL(`classrooms/${classroomId}/users`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url, {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving users:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((user: any) => User.fromJson(user));
		} catch (error) {
			console.error('An error occurred while retrieving projects:', error);
			return null;
		}
	}

	public static async getClassroomProjects(classroomId: string, token: string): Promise<Project[] | null> {
		try {
			const url = new URL(`classrooms/${classroomId}/projects`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url, {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving projects:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((project: any) => Project.fromJson(project));
		} catch (error) {
			console.error('An error occurred while retrieving projects:', error);
			return null;
		}
	}

	public static async getClassroomProjectSubmissions(classroomId: string, token: string): Promise<ProjectSubmission[] | null> {
		try {
			const url = new URL(`classrooms/${classroomId}/project-submissions`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url, {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving projects:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((projectSubmission: any) => ProjectSubmission.fromJson(projectSubmission));
		} catch (error) {
			console.error('An error occurred while retrieving projects:', error);
			return null;
		}
	}

	public static async getAllClassrooms(token: string): Promise<Classroom[] | null> {
		try {
			const url = new URL("classrooms", appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving classrooms:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((classroom: any) => {
				const loadedClassroom = Classroom.fromJson(classroom);
				loadedClassroom.loadClassroomAdmins(classroom.classroomAdmins?.map((admin: any) => User.fromJson(admin)));
				loadedClassroom.loadClassroomStudents(classroom.students?.map((student: any) => User.fromJson(student)));
				return loadedClassroom;
			});
		} catch (error) {
			console.error('An error occurred while retrieving classrooms:', error);
			return null;
		}
	}

	public static async getClassroom(classroomId: string, token: string): Promise<Classroom> {
		try {
			const url = new URL(`classrooms/${classroomId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				throw new Error("Error retrieving classroom info. Response status: " + response.status + " " + response.statusText);
			}

			const data = await response.json();
			const classroom = Classroom.fromJson(data);
			classroom.loadClassroomAdmins(data.classroomAdmins?.map((admin: any) => User.fromJson(admin)));
			classroom.loadClassroomStudents(data.students?.map((student: any) => User.fromJson(student)));
			classroom.loadProjects(data.projects?.map((project: any) => Project.fromJson(project)));
			return classroom;
		} catch (error) {
			throw error;
		}
	}

	public static async deleteClassroom(classroomId: string, token: string): Promise<void> {
		try {
			const url = new URL(`classrooms/${classroomId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'DELETE',
				headers: ConductionAPI.getAuthHeaders(token)
			});

			if (!response.ok) {
				throw new Error(`Error occurred during classroom deletion: ${response.status} ${response.statusText}`);
			}
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			throw new Error(`An error occurred while deleting classroom: ${errorMessage}`);
		}
	}

	public static async createClassroom(classroomForm: ClassroomForm, token: string) {
		const url = new URL(`classrooms`, appSettings.ConductionApiBaseUrl);

		const classroomFormString = JSON.stringify(classroomForm);

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: classroomFormString
		});

		if (!response.ok) {
			throw new Error(`Error creating classroom: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async duplicateClassroom(classroomId: string, newClassroomName: string, token: string) {
		const url = new URL(`classrooms/${classroomId}/duplicate/${newClassroomName}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error duplicating classroom: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async renameClassroom(classroomId: string, newName: string, token: string) {
		const url = new URL(`classrooms/${classroomId}/rename/${newName}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error updating classroom name: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async setClassroomAdminLimit(classroomId: string, newLimit: number, token: string) {
		const url = new URL(`classrooms/${classroomId}/classroom-admin-limit/${newLimit}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error updating classroom admin limit: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async setStudentLimit(classroomId: string, newLimit: number, token: string) {
		const url = new URL(`classrooms/${classroomId}/student-limit/${newLimit}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error updating student limit: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async getSubmissionTokens(type: "classroom" | "user", id: string, token: string): Promise<ProjectSubmissionToken[] | null> {
		try {
			const modelPath = type == "classroom" ? "classrooms" : type == "user" ? "users" : undefined;
			if (modelPath == undefined) {
				throw new Error(`Unexpected model type: ${type}`)
			}

			const url = new URL(`${modelPath}/${id}/submission-credits`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error(`Error retrieving ${type} submission credits:`, response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((tokenJson: any) => ProjectSubmissionToken.fromJson(tokenJson));
		} catch (error) {
			console.error(`An error occurred while retrieving ${type} submission credits:`, error);
			return null;
		}
	}

	public static async addSubmissionToken(type: "classroom" | "user", id: string, tokenForm: SubmissionTokenForm, token: string) {
		const modelPath = type == "classroom" ? "classrooms" : type == "user" ? "users" : undefined;
		if (modelPath == undefined) {
			throw new Error(`Unexpected model type: ${type}`)
		}
		const url = new URL(`${modelPath}/${id}/submission-credits`, appSettings.ConductionApiBaseUrl);

		const tokenFormString = JSON.stringify(tokenForm);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
			body: tokenFormString
		});

		if (!response.ok) {
			throw new Error(`Error adding credits to ${type}: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async removeSubmissionToken(type: "classroom" | "user", id: string, tokenId: string, token: string) {
		const modelPath = type == "classroom" ? "classrooms" : type == "user" ? "users" : undefined;
		if (modelPath == undefined) {
			throw new Error(`Unexpected model type: ${type}`)
		}

		const url = new URL(`${modelPath}/${id}/submission-credits/${tokenId}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'DELETE',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error removing credits from ${type}: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async balanceSubmissionTokens(type: "classroom" | "user", id: string, token: string): Promise<BalancingResult[]> {
		const modelPath = type == "classroom" ? "classrooms" : type == "user" ? "users" : undefined;
		if (modelPath == undefined) {
			throw new Error(`Unexpected model type: ${type}`)
		}

		const url = new URL(`${modelPath}/${id}/submission-credits`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PATCH',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error balancing credits to ${type}: ${response.status} ${response.statusText}`);
		}

		return await response.json();
	}

	// Below are several methods to support multipart upload to S3 with retries and on-demand re-signing of parts.
	private static async sleep(ms: number) { return new Promise(r => setTimeout(r, ms)); }

	/**
	 * PUT a single multipart part with retries and on-demand re-sign for 403/400.
	 * getUrl() should return the current presigned URL (re-sign if missing/expired).
	 */
	private static async putPartWithRetry(
		partNumber: number,
		getUrl: () => Promise<string>,
		blob: Blob,
		maxAttempts = 5,
		abortSignal?: AbortSignal
	): Promise<string> {
		let attempt = 0;
		while (true) {
			if (abortSignal?.aborted) {
				throw new DOMException("Aborted", "AbortError");
			}

			attempt++;
			try {
				const url = await getUrl();
				const resp = await fetch(url, { method: 'PUT', body: blob, signal: abortSignal });

				if (resp.ok) {
					const etag = resp.headers.get('ETag');
					if (!etag) throw new Error('Missing ETag on S3 response. Ensure CORS exposes "ETag".');
					return Utils.replaceAll(etag, '"', '');
				}

				// Expired/invalid presign -> re-sign and retry
				if (resp.status === 403 || resp.status === 400) {
					if (attempt >= maxAttempts) {
						const txt = await resp.text().catch(() => '');
						throw new Error(`S3 ${resp.status} after ${attempt} attempts for part ${partNumber}: ${txt}`);
					}
				}
				// Transient S3/Net errors
				else if (resp.status >= 500) {
					if (attempt >= maxAttempts) {
						const txt = await resp.text().catch(() => '');
						throw new Error(`S3 error ${resp.status}: ${resp.statusText}. ${txt}`);
					}
				}
				else {
					const txt = await resp.text().catch(() => '');
					throw new Error(`S3 part upload failed (${resp.status}) ${resp.statusText} ${txt}`);
				}
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") throw err; // bubble up cancel
				if (attempt >= maxAttempts) throw err;
			}

			const backoffMs = Math.min(30_000, 500 * 2 ** (attempt - 1)) + Math.random() * 250;
			// Sleep is also cancelable
			await Promise.race([
				this.sleep(backoffMs),
				new Promise((_, reject) => abortSignal?.addEventListener("abort", () => reject(new DOMException("Aborted", "AbortError")), { once: true }))
			]);
		}
	}

	// --- 1) create-multipart
	public static async createMultipartUpload(
		category: "SubmissionVideo" | "InstrumentalistTaskVideo" | "FeedbackVideo",
		entityId: string,
		file: File,
		token: string
	): Promise<CreateMultipartResponseDto> {
		const url = new URL(`uploads/create-multipart`, appSettings.PerformanceApiBaseUrl);

		const body = {
			category,
			entityId,
			fileName: file.name || "upload",
			contentType: file.type || "application/octet-stream",
			contentLength: file.size
		};

		const resp = await fetch(url.toString(), {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(body)
		});

		if (!resp.ok) {
			const text = await resp.text().catch(() => "");
			throw new Error(`Failed to create multipart upload: (${resp.status}) ${resp.statusText} ${text}`);
		}

		const data = (await resp.json()) as CreateMultipartResponseDto;
		if (!data?.key || !data?.uploadId || !data?.partSizeBytes) {
			throw new Error("Malformed create-multipart response.");
		}
		return data;
	}

	// --- 2) sign parts
	public static async signUploadParts(
		key: string,
		uploadId: string,
		partNumbers: number[],
		token: string
	): Promise<SignedPartUrl[]> {
		const url = new URL(`uploads/sign-parts`, appSettings.PerformanceApiBaseUrl);
		const resp = await fetch(url.toString(), {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify({ key, uploadId, partNumbers })
		});
		if (!resp.ok) {
			const t = await resp.text().catch(() => "");
			throw new Error(`Failed to sign parts: (${resp.status}) ${resp.statusText} ${t}`);
		}
		const data = await resp.json();
		return (data?.urls ?? []) as SignedPartUrl[];
	}

	// --- 3) complete-multipart
	public static async completeMultipartUpload(
		key: string,
		uploadId: string,
		parts: CompletedPart[],
		token: string
	): Promise<void> {
		if (!Array.isArray(parts) || parts.length === 0) {
			throw new Error("No parts to complete.");
		}
		// Deduplicate by partNumber (last write wins), then sort ascending as S3 expects
		const byNumber = new Map<number, string>();
		for (const p of parts) if (p && Number.isFinite(p.partNumber)) byNumber.set(p.partNumber, p.eTag);
		const normalized = Array.from(byNumber.entries())
			.map(([partNumber, eTag]) => ({ partNumber, eTag }))
			.sort((a, b) => a.partNumber - b.partNumber);

		const url = new URL(`uploads/complete-multipart`, appSettings.PerformanceApiBaseUrl);
		const body = { key, uploadId, parts: normalized };

		const resp = await fetch(url.toString(), {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(body),
		});

		if (!resp.ok) {
			const text = await resp.text().catch(() => "");
			throw new Error(`Failed to complete multipart upload: (${resp.status}) ${resp.statusText} ${text}`);
		}
	}

	// --- 4) high-level multipart upload with retry, abort, prefetch-next-batch, coarse progress ---
	public static async uploadFileMultipart(
		category: "SubmissionVideo" | "InstrumentalistTaskVideo" | "FeedbackVideo",
		entityId: string,
		file: File,
		token: string,
		options?: {
			concurrency?: number;
			onProgress?: (uploadedBytes: number) => void; // coarse: fires after each part completes
			batchSize?: number;
			signal?: AbortSignal;
		}
	): Promise<{ key: string; uploadId: string; parts: CompletedPart[] }> {
		const signal = options?.signal;

		if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

		const { key, uploadId, partSizeBytes } = await this.createMultipartUpload(category, entityId, file, token /* (optionally pass signal) */);

		const size = file.size, partSize = partSizeBytes;
		const totalParts = Math.ceil(size / partSize);
		const plan = Array.from({ length: totalParts }, (_, i) => ({
			partNumber: i + 1,
			start: i * partSize,
			end: Math.min((i + 1) * partSize, size)
		}));

		const concurrency = Math.max(1, Math.min(options?.concurrency ?? 3, 8));
		const batchSize = Math.max(1, Math.min(options?.batchSize ?? 10, 100));

		const completed: CompletedPart[] = [];
		let uploadedBytes = 0;
		options?.onProgress?.(0); // initial tick

		let offset = 0;
		let nextSignedPromise: Promise<SignedPartUrl[]> | null = null;

		// Helper that respects abort while awaiting a promise
		const withAbort = async <T,>(p: Promise<T>): Promise<T> => {
			if (!signal) return p;
			if (signal.aborted) throw new DOMException("Aborted", "AbortError");
			return await Promise.race([
				p,
				new Promise<never>((_, reject) => signal.addEventListener("abort", () => reject(new DOMException("Aborted", "AbortError")), { once: true }))
			]);
		};

		while (offset < plan.length) {
			if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

			const batch = plan.slice(offset, offset + batchSize);

			// Use prefetched URLs if available; otherwise sign now (optionally pass signal to your fetch)
			let signed = nextSignedPromise
				? await withAbort(nextSignedPromise)
				: await withAbort(this.signUploadParts(key, uploadId, batch.map(b => b.partNumber), token));

			// Prefetch next batch immediately
			const nextBatch = plan.slice(offset + batchSize, offset + 2 * batchSize);
			nextSignedPromise = nextBatch.length
				? this.signUploadParts(key, uploadId, nextBatch.map(b => b.partNumber), token) // you can wrap with withAbort() when awaited
				: null;

			// URL cache for current batch; re-sign on demand if missing/expired
			const urlCache = new Map<number, string>(signed.map(s => [s.partNumber, s.url]));
			const getUrl = async (pn: number): Promise<string> => {
				if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
				const existing = urlCache.get(pn);
				if (existing) return existing;
				const fresh = await withAbort(this.signUploadParts(key, uploadId, [pn], token));
				const u = fresh[0]?.url;
				if (!u) throw new Error(`Failed to re-sign part ${pn}`);
				urlCache.set(pn, u);
				return u;
			};

			// Upload this batch with limited concurrency
			const queue = [...batch];
			const workers: Promise<void>[] = [];
			const doWork = async () => {
				while (queue.length) {
					if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
					const p = queue.shift()!;
					const blob = file.slice(p.start, p.end);

					const eTag = await this.putPartWithRetry(
						p.partNumber,
						() => getUrl(p.partNumber),
						blob,
						5,
						signal // << pass AbortSignal into the PUT fetch
					);

					completed.push({ partNumber: p.partNumber, eTag });
					uploadedBytes += blob.size;
					options?.onProgress?.(uploadedBytes); // coarse tick
				}
			};
			for (let i = 0; i < concurrency; i++) workers.push(doWork());
			await Promise.all(workers);

			offset += batch.length;
		}

		// Dedupe/sort guardrails before complete
		const byNumber = new Map<number, string>();
		for (const p of completed) byNumber.set(p.partNumber, p.eTag);
		const normalized = Array.from(byNumber.entries())
			.map(([partNumber, eTag]) => ({ partNumber, eTag }))
			.sort((a, b) => a.partNumber - b.partNumber);

		if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
		await this.completeMultipartUpload(key, uploadId, normalized, token /* (optionally pass signal) */);

		options?.onProgress?.(size); // final tick: 100%

		return { key, uploadId, parts: normalized };
	}

	/**
	 * Submit an instrumentalist task:
	 * 1) Upload file to S3 (multipart)
	 * 2) Tell API to process the uploaded object (rawKey)
	 */
	public static async submitInstrumentalistTask(
		instrumentalistTaskId: string,
		videoFile: File,
		token: string,
		options?: { onProgress?: (uploadedBytes: number) => void; signal?: AbortSignal }
	): Promise<void> {
		// Step 1: upload to S3 under Temp/InstrumentalistTaskVideo/{taskId}/...
		const { key } = await this.uploadFileMultipart(
			"InstrumentalistTaskVideo",
			instrumentalistTaskId,
			videoFile,
			token,
			{ concurrency: 3, batchSize: 10, onProgress: options?.onProgress, signal: options?.signal }
		);

		// Step 2: call API with rawKey + declaredBytes
		const url = new URL(`instrumentalist-tasks/${instrumentalistTaskId}`, appSettings.PerformanceApiBaseUrl);
		const body = { rawKey: key, declaredBytes: videoFile.size };

		const resp = await fetch(url.toString(), {
			method: "POST",
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(body)
		});

		if (!resp.ok) {
			// try json first, then text
			let msg = "";
			try { const j = await resp.json(); msg = j?.error || j?.message || ""; } catch { msg = await resp.text().catch(() => ""); }
			throw new Error(`Error submitting instrumentalist task: (${resp.status}) ${resp.statusText} ${msg}`);
		}
	}

	/**
	 * Submit instrumentalist feedback video:
	 * 1) Upload file to S3 (multipart)
	 * 2) Tell API to process the uploaded object (rawKey)
	 */
	public static async submitInstrumentalistFeedback(
		submissionId: string,
		videoFile: File,
		token: string,
		options?: { onProgress?: (uploadedBytes: number) => void; signal?: AbortSignal }
	): Promise<void> {
		const { key } = await this.uploadFileMultipart(
			"FeedbackVideo",
			submissionId,
			videoFile,
			token,
			{ concurrency: 3, batchSize: 10, onProgress: options?.onProgress, signal: options?.signal }
		);

		const url = new URL(`instrumentalist-feedback/${submissionId}`, appSettings.PerformanceApiBaseUrl);
		const body = { rawKey: key, declaredBytes: videoFile.size };

		const resp = await fetch(url.toString(), {
			method: "POST",
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(body)
		});

		if (!resp.ok) {
			let msg = "";
			try { const j = await resp.json(); msg = j?.error || j?.message || ""; } catch { msg = await resp.text().catch(() => ""); }
			throw new Error(`Error submitting instrumentalist feedback: (${resp.status}) ${resp.statusText} ${msg}`);
		}
	}

	/**
	 * Fulfill a project submission:
	 * 1) Upload file to S3 (multipart)
	 * 2) Tell API to process the uploaded object (rawKey)
	 */
	public static async fulfillProjectSubmission(
		submissionId: string,
		videoFile: File,
		token: string,
		options?: { onProgress?: (uploadedBytes: number) => void; signal?: AbortSignal }
	): Promise<void> {
		const { key } = await this.uploadFileMultipart(
			"SubmissionVideo",
			submissionId,
			videoFile,
			token,
			{ concurrency: 3, batchSize: 10, onProgress: options?.onProgress, signal: options?.signal }
		);

		const url = new URL(`submissions/${submissionId}/fulfill`, appSettings.PerformanceApiBaseUrl);
		const body = { rawKey: key, declaredBytes: videoFile.size };

		const resp = await fetch(url.toString(), {
			method: "POST",
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(body)
		});

		if (!resp.ok) {
			let msg = "";
			try { const j = await resp.json(); msg = j?.error || j?.message || ""; } catch { msg = await resp.text().catch(() => ""); }
			throw new Error(`Error fulfilling project submission: (${resp.status}) ${resp.statusText} ${msg}`);
		}
	}

	public static async getActivityFeedPage(
		token: string,
		page: number,
		pageSize: number,
		classroomId?: string,
		associatedUserId?: string
	): Promise<PagedResults<ActivityFeedItem>> {
		// Build URL with query params
		const url = new URL(`${appSettings.ConductionApiBaseUrl}activity-feed`);
		url.searchParams.set('page', String(Math.max(1, page || 1)));
		url.searchParams.set('pageSize', String(Math.max(1, pageSize || 12)));
		url.searchParams.set('classroomId', classroomId ?? '');
		url.searchParams.append('associatedUserId', associatedUserId ?? "");

		const resp = await fetch(url.toString(), {
			method: 'GET',
			headers: this.getAuthHeaders(token),
		});

		if (!resp.ok) {
			// try to surface a useful message
			let msg = `(${resp.status}) ${resp.statusText}`;
			try {
				const j = await resp.json();
				if (typeof j?.error === 'string') msg = j.error;
				else if (typeof j?.message === 'string') msg = j.message;
			} catch {
				// ignore parse errors; keep default msg
			}
			throw new Error(`Error retrieving activity feed: ${msg}`);
		}

		const data = await resp.json();

		// Handle both camelCase and PascalCase from server just in case
		const rawItems = (data.items ?? data.Items ?? []) as any[];

		const items: ActivityFeedItem[] = rawItems.map((it: any) => {
			const kind: number =
				it.kind ?? it.Kind ?? ActivityFeedItemKind.Submission;

			const eventDate: string | null =
				it.eventDate ?? it.EventDate ?? null;

			// Prefer camelCase, fallback to PascalCase
			const subRaw = it.submission ?? it.Submission;
			const pubRaw = it.publication ?? it.Publication;

			if (kind === ActivityFeedItemKind.Publication) {
				return {
					kind: ActivityFeedItemKind.Publication,
					eventDate,
					publication: ProjectPublication.fromJson(pubRaw),
				};
			} else {
				return {
					kind: ActivityFeedItemKind.Submission,
					eventDate,
					submission: ProjectSubmission.fromJson(subRaw),
				};
			}
		});

		return {
			items,
			page: data.page ?? data.Page ?? 1,
			pageSize: data.pageSize ?? data.PageSize ?? items.length,
			totalCount: data.totalCount ?? data.TotalCount ?? items.length,
			totalPages: data.totalPages ?? data.TotalPages ?? 1,
			hasPrevious: data.hasPrevious ?? data.HasPrevious ?? false,
			hasNext: data.hasNext ?? data.HasNext ?? false,
		};
	}

	public static async getInstrumentalistProjectSubmissions(
		token: string,
		userId: string,
	): Promise<PagedResults<ProjectSubmission>> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}submissions-for-instrumentalist/${userId}`);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				throw new Error(`Error retrieving projects: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();
			return {
				items: data.items.map((submission: any) => ProjectSubmission.fromJson(submission)),
				page: data.page,
				pageSize: data.pageSize,
				totalCount: data.totalCount,
				totalPages: data.totalPages,
				hasPrevious: data.hasPrevious,
				hasNext: data.hasNext
			};
		} catch (error) {
			throw new Error(`An error occurred while retrieving projects: ${error}`);
		}
	}

	public static async getVideoEditorProjectSubmissions(
		token: string,
		userId: string,
	): Promise<PagedResults<ProjectSubmission>> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}submissions-for-editor/${userId}`);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				throw new Error(`Error retrieving projects: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();
			return {
				items: data.items.map((submission: any) => ProjectSubmission.fromJson(submission)),
				page: data.page,
				pageSize: data.pageSize,
				totalCount: data.totalCount,
				totalPages: data.totalPages,
				hasPrevious: data.hasPrevious,
				hasNext: data.hasNext
			};
		} catch (error) {
			throw new Error(`An error occurred while retrieving projects: ${error}`);
		}
	}

	public static async getProjectSubmissionsPage(
		token: string,
		pageNumber: number,
		pageSize: number,
		approvedOnly: boolean,
		unapprovedOnly: boolean,
		fulfilledOnly: boolean,
		associatedUserId: string | undefined,
		fulfilledTasksOnly: boolean,
		unfulfilledTasksOnly: boolean,
		classroomId: string | undefined,
		loadOffers: boolean = false
	): Promise<PagedResults<ProjectSubmission>> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}submissions`);
			url.searchParams.append('page', pageNumber.toString());
			url.searchParams.append('pageSize', pageSize.toString());
			url.searchParams.append('approvedOnly', approvedOnly.toString());
			url.searchParams.append('unapprovedOnly', unapprovedOnly.toString());
			url.searchParams.append('fulfilledOnly', fulfilledOnly.toString());
			url.searchParams.append('associatedUserId', associatedUserId ?? "");
			url.searchParams.append('fulfilledTasksOnly', fulfilledTasksOnly.toString());
			url.searchParams.append('unfulfilledTasksOnly', unfulfilledTasksOnly.toString());
			url.searchParams.append('classroomId', classroomId ?? "");
			url.searchParams.append('loadOffers', loadOffers.toString());

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				throw new Error(`Error retrieving projects: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();
			return {
				items: data.items.map((submission: any) => ProjectSubmission.fromJson(submission)),
				page: data.page,
				pageSize: data.pageSize,
				totalCount: data.totalCount,
				totalPages: data.totalPages,
				hasPrevious: data.hasPrevious,
				hasNext: data.hasNext
			};
		} catch (error) {
			throw new Error(`An error occurred while retrieving projects: ${error}`);
		}
	}

	public static async claimInstrumentalistTask(
		token: string,
		taskId: string
	): Promise<{ success: boolean; alreadyClaimed?: boolean; message?: string }> {
		try {
			const url = `${appSettings.ConductionApiBaseUrl}instrumentalist-tasks/${taskId}/claim`;

			const response = await fetch(url, {
				method: 'POST',
				headers: this.getAuthHeaders(token),
			});

			if (response.status === 400) {
				// likely already claimed
				const msg = await response.text();
				return { success: false, alreadyClaimed: true, message: msg || 'This task has already been claimed.' };
			}

			if (!response.ok) {
				const msg = await response.text();
				throw new Error(`Error claiming instrumentalist task: ${response.status} ${response.statusText} - ${msg}`);
			}

			return { success: true };
		} catch (error) {
			throw new Error(`An error occurred while claiming instrumentalist task: ${error}`);
		}
	}

	public static async claimVideoEditorSubmission(
		token: string,
		projectSubmissionId: string
	): Promise<{ success: boolean; alreadyClaimed?: boolean; message?: string }> {
		try {
			const url = `${appSettings.ConductionApiBaseUrl}video-editor/submissions/${projectSubmissionId}/claim`;

			const response = await fetch(url, {
				method: 'POST',
				headers: this.getAuthHeaders(token),
			});

			if (response.status === 400) {
				const msg = await response.text();
				return { success: false, alreadyClaimed: true, message: msg || 'This submission has already been claimed.' };
			}

			if (!response.ok) {
				const msg = await response.text();
				throw new Error(`Error claiming video editor submission: ${response.status} ${response.statusText} - ${msg}`);
			}

			return { success: true };
		} catch (error) {
			throw new Error(`An error occurred while claiming video editor submission: ${error}`);
		}
	}

	// --- Instrumentalists & Video Editors ---

	/** GET /instrumentalists — ConductionAdmin or InstrumentalistLiaison */
	public static async getAllInstrumentalists(token: string): Promise<User[] | null> {
		try {
			const url = new URL(`instrumentalists`, appSettings.ConductionApiBaseUrl);
			const resp = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!resp.ok) {
				console.error('Error retrieving instrumentalists:', resp.status, resp.statusText);
				return null;
			}

			const data = await resp.json();
			// API returns UserInfo DTO; assuming User.fromJson can consume it.
			return data.map((u: any) => User.fromJson(u));
		} catch (err) {
			console.error('An error occurred while retrieving instrumentalists:', err);
			return null;
		}
	}

	/** GET /video-editors — ConductionAdmin or InstrumentalistLiaison */
	public static async getAllVideoEditors(token: string): Promise<User[] | null> {
		try {
			const url = new URL(`video-editors`, appSettings.ConductionApiBaseUrl);
			const resp = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!resp.ok) {
				console.error('Error retrieving video editors:', resp.status, resp.statusText);
				return null;
			}

			const data = await resp.json();
			return data.map((u: any) => User.fromJson(u));
		} catch (err) {
			console.error('An error occurred while retrieving video editors:', err);
			return null;
		}
	}

	/** PUT /instrumentalists/{instrumentalistId}/active/{active} — ConductionAPI
	 *  Roles: ConductionAdmin, InstrumentalistLiaison
	 */
	public static async setInstrumentalistActiveStatus(
		instrumentalistId: string,
		active: boolean,
		token: string
	): Promise<void> {
		const url = new URL(
			`instrumentalists/${encodeURIComponent(instrumentalistId)}/active/${active}`,
			appSettings.ConductionApiBaseUrl
		);

		const resp = await fetch(url.toString(), {
			method: "PUT",
			headers: ConductionAPI.getAuthHeaders(token),
		});

		if (!resp.ok) {
			let message = `(${resp.status}) ${resp.statusText}`;
			try {
				const j = await resp.json();
				if (typeof j?.error === "string") message = j.error;
				else if (typeof j?.message === "string") message = j.message;
			} catch { /* ignore */ }
			throw new Error(`Error updating instrumentalist active status: ${message}`);
		}
	}

	public static async updateUserLocation(userId: string, locationId: string | null, token: string): Promise<void> {
		const url = new URL(
			`users/${encodeURIComponent(userId)}/location/${locationId ? encodeURIComponent(locationId) : ""}`,
			appSettings.ConductionApiBaseUrl
		);

		const resp = await fetch(url.toString(), {
			method: "PUT",
			headers: ConductionAPI.getAuthHeaders(token),
		});
		if (!resp.ok) {
			let message = `(${resp.status}) ${resp.statusText}`;
			try {
				const j = await resp.json();
				if (typeof j?.error === "string") message = j.error;
				else if (typeof j?.message === "string") message = j.message;
			} catch { /* ignore */ }
			throw new Error(`Error updating user location: ${message}`);
		}
	}

	// --- Classroom ↔ Instrumentalist assignments ---

	/** PUT /instrumentalists/{instrumentalistId}/classrooms
	 *  Replaces the instrumentalist's classroom assignments with the provided list.
	 *  Roles: ConductionAdmin, InstrumentalistLiaison
	 */
	public static async setInstrumentalistClassrooms(
		instrumentalistId: string,
		classroomIds: string[],
		token: string
	): Promise<void> {
		const url = new URL(
			`instrumentalists/${encodeURIComponent(instrumentalistId)}/classrooms`,
			appSettings.ConductionApiBaseUrl
		);

		const resp = await fetch(url.toString(), {
			method: "PUT",
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(classroomIds ?? []),
		});

		if (!resp.ok) {
			// Try to surface a useful server message
			let message = `(${resp.status}) ${resp.statusText}`;
			try {
				const ct = resp.headers.get("content-type") || "";
				if (ct.includes("application/json")) {
					const j = await resp.json();
					if (typeof j?.error === "string") message = j.error;
					else if (typeof j?.message === "string") message = j.message;
				} else {
					const t = await resp.text();
					if (t) message = t;
				}
			} catch {
				/* ignore parse errors; keep default message */
			}
			throw new Error(`Error setting instrumentalist classrooms: ${message}`);
		}
	}

	public static async generateProjectSubmissionTasks(projectSubmissionId: string, token: string): Promise<void> {
		const url = new URL(`submissions/${projectSubmissionId}/generate`, appSettings.PerformanceApiBaseUrl);
		const response = await fetch(url.toString(), {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(`Error generating project submission tasks: ${errorData.error || response.statusText}`);
		}
	}

	/** PUT /instrumentalist-tasks/{taskId}/instrumentalist/{userId} — PerformanceAPI
	 *  Roles: ConductionAdmin, InstrumentalistLiaison
	 */
	public static async changeTaskInstrumentalist(
		instrumentalistTaskId: string,
		newInstrumentalistId: string,
		token: string
	): Promise<void> {
		const url = new URL(
			`instrumentalist-tasks/${encodeURIComponent(instrumentalistTaskId)}/instrumentalist/${encodeURIComponent(newInstrumentalistId)}`,
			appSettings.PerformanceApiBaseUrl
		);

		const resp = await fetch(url.toString(), {
			method: "PUT",
			headers: ConductionAPI.getAuthHeaders(token),
		});

		if (!resp.ok) {
			let message = `(${resp.status}) ${resp.statusText}`;
			try {
				const j = await resp.json();
				if (typeof j?.error === "string") message = j.error;
				else if (typeof j?.message === "string") message = j.message;
			} catch { /* no-op */ }
			throw new Error(`Error changing instrumentalist on task: ${message}`);
		}
	}

	/** PUT /submissions/{projectSubmissionId}/editor/{userId} — PerformanceAPI
	 *  Roles: ConductionAdmin, InstrumentalistLiaison
	 */
	public static async changeSubmissionEditor(
		projectSubmissionId: string,
		newEditorId: string,
		token: string
	): Promise<void> {
		const url = new URL(
			`submissions/${encodeURIComponent(projectSubmissionId)}/editor/${encodeURIComponent(newEditorId)}`,
			appSettings.PerformanceApiBaseUrl
		);

		const resp = await fetch(url.toString(), {
			method: "PUT",
			headers: ConductionAPI.getAuthHeaders(token),
		});

		if (!resp.ok) {
			let message = `(${resp.status}) ${resp.statusText}`;
			try {
				const j = await resp.json();
				if (typeof j?.error === "string") message = j.error;
				else if (typeof j?.message === "string") message = j.message;
			} catch { /* no-op */ }
			throw new Error(`Error changing submission editor: ${message}`);
		}
	}

	/** PUT /submissions/{projectSubmissionId}/feedback-provider/{userId} — PerformanceAPI
	 *  Roles: ConductionAdmin, InstrumentalistLiaison
	 */
	public static async changeSubmissionFeedbackProvider(
		projectSubmissionId: string,
		newFeedbackProviderId: string,
		token: string
	): Promise<void> {
		const url = new URL(
			`submissions/${encodeURIComponent(projectSubmissionId)}/feedback-provider/${encodeURIComponent(newFeedbackProviderId)}`,
			appSettings.PerformanceApiBaseUrl
		);

		const resp = await fetch(url.toString(), {
			method: "PUT",
			headers: ConductionAPI.getAuthHeaders(token),
		});

		if (!resp.ok) {
			let message = `(${resp.status}) ${resp.statusText}`;
			try {
				const j = await resp.json();
				if (typeof j?.error === "string") message = j.error;
				else if (typeof j?.message === "string") message = j.message;
			} catch { /* no-op */ }
			throw new Error(`Error changing submission feedback provider: ${message}`);
		}
	}

	public static async approveProjectSubmission(projectSubmissionId: string, token: string): Promise<void> {
		const url = new URL(`submissions/${projectSubmissionId}/approve`, appSettings.PerformanceApiBaseUrl);
		const response = await fetch(url.toString(), {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(`Error approving project submission: ${errorData.error || response.statusText}`);
		}
	}

	public static async getAllLocations(token: string): Promise<Location[] | null> {
		try {
			const url = new URL(`locations`, appSettings.ConductionApiBaseUrl);
			const resp = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!resp.ok) {
				console.error('Error retrieving locations:', resp.status, resp.statusText);
				return null;
			}

			const data = await resp.json();
			return data.map((u: any) => Location.fromJson(u));
		} catch (err) {
			console.error('An error occurred while retrieving locations:', err);
			return null;
		}
	}

	public static async exportProjectToMusicXml(project: Project, token: string): Promise<FileResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}export/musicxml`;

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(project.toJson())
		});

		let fileResponse: FileResponse;
		if (response.ok) {
			const musicXmlBlob = await response.blob();
			const blobUrl = window.URL.createObjectURL(musicXmlBlob);
			fileResponse = {
				fileBlobUrl: blobUrl,
				success: true,
				message: ''
			};
		} else {
			console.error(`Failed to download MusicXml.`);
			fileResponse = {
				fileBlobUrl: '',
				success: false,
				message: `An error occurred while exporting this project: (${response.status}) ${response.statusText}`
			};
		}

		return fileResponse;
	}

	public static async exportProjectToMp3(project: Project, token: string): Promise<FileResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}export/mp3`;

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(project.toJson())
		});

		let fileResponse: FileResponse;
		if (response.ok) {
			const mp3Blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(mp3Blob);
			fileResponse = {
				fileBlobUrl: blobUrl,
				success: true,
				message: ''
			};
		} else {
			console.error(`Failed to download MP3.`);
			fileResponse = {
				fileBlobUrl: '',
				success: false,
				message: `An error occurred while exporting this project: (${response.status}) ${response.statusText}`
			};
		}

		return fileResponse;
	}

	public static async exportProjectToPdf(project: Project, token: string): Promise<FileResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}export/pdf`;

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(project.toJson())
		});

		let fileResponse: FileResponse;
		if (response.ok) {
			const pdfBlob = await response.blob();
			const blobUrl = window.URL.createObjectURL(pdfBlob);
			fileResponse = {
				fileBlobUrl: blobUrl,
				success: true,
				message: ''
			};
		} else {
			console.error(`Failed to download PDF.`);
			fileResponse = {
				fileBlobUrl: '',
				success: false,
				message: `An error occurred while exporting this project: (${response.status}) ${response.statusText}`
			};
		}

		return fileResponse;
	}

	public static async exportProjectToZip(project: Project, token: string): Promise<FileResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}export/zip`;

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(project.toJson())
		});

		let fileResponse: FileResponse;
		if (response.ok) {
			const zipBlib = await response.blob();
			const blobUrl = window.URL.createObjectURL(zipBlib);
			fileResponse = {
				fileBlobUrl: blobUrl,
				success: true,
				message: ''
			};
		} else {
			console.error(`Failed to download ZIP.`);
			fileResponse = {
				fileBlobUrl: '',
				success: false,
				message: `An error occurred while exporting this project: (${response.status}) ${response.statusText}`
			};
		}

		return fileResponse;
	}

	public static async sendToMusicians(projectId: string, username: string, password: string, token: string): Promise<ActionResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}submit`;

		const payload = {
			projectId,
			username,
			password
		};

		const endpointCall = await ConductionAPI.callEndpoint(url, 'PUT', payload, token);

		const message = endpointCall && endpointCall.response ? await endpointCall.response.text() : "";

		if (endpointCall.response && !endpointCall.response.ok) {
			return {
				success: false,
				message: message
			};
		}

		if (endpointCall.errorMessage != undefined) {
			return {
				success: false,
				message: endpointCall.errorMessage,
				errorDetails: endpointCall.errorDetails
			};
		}

		return {
			success: true,
			message: message
		};
	}


	public static async setTutorialCompletionStatus(userId: string, completionStatus: boolean, token: string) {
		const url = new URL(`users/${userId}/beginner-tutorial/${completionStatus}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error setting tutorial competion status: ${response.status} ${response.statusText}`);
		}

		return;
	}

	public static async validateAccessCode(accessCode: string): Promise<AccessCodeValidationInfo> {
		try {
			const response = await fetch(`${appSettings.ConductionApiBaseUrl}classrooms/${accessCode}/validate`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json'
				}
			});

			if (!response.ok) {
				return {
					isValid: false,
					accessCode: accessCode,
					classroomId: "",
					classroomName: "",
					isStudentAccessCode: false,
					isClassroomAdminAccessCode: false,
					isConductionAdminAccessCode: false,
					isLimitReached: false
				};
			}

			const data: AccessCodeValidationInfo = await response.json();
			return {
				isValid: true,
				accessCode: data.accessCode,
				classroomId: data.classroomId,
				classroomName: data.classroomName,
				isStudentAccessCode: data.isStudentAccessCode,
				isClassroomAdminAccessCode: data.isClassroomAdminAccessCode,
				isConductionAdminAccessCode: data.isConductionAdminAccessCode,
				isLimitReached: data.isLimitReached
			};
		} catch (error) {
			console.error('Error validating access code:', error);
			throw error;
		}
	}

	public static async requestPasswordReset(emailAddress: string): Promise<boolean> {
		const url = new URL("forgot-password", appSettings.PerformanceApiBaseUrl);

		const payload = {
			email: emailAddress
		};

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		if (!response.ok) {
			console.error('Error sending forgot password request:', response.status, response.statusText);
			return false;
		}

		return true;
	}

	public static async resetPassword(userId: string, token: string, newPassword: string): Promise<boolean> {
		const url = new URL(`reset-password`, appSettings.PerformanceApiBaseUrl);

		const payload = {
			userId,
			token,
			newPassword
		};

		const response = await fetch(url, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload)
		});

		if (!response.ok) {
			console.error('Error resetting password:', response.status, response.statusText);
			return false;
		}

		return true;
	}

	public static async registerUser(username: string, email: string, firstName: string, lastName: string, password: string, confirmPassword: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}register/user`;
		const payload = {
			username,
			email,
			firstName,
			lastName,
			password,
			confirmPassword,
		};

		try {
			const endpointCall = await ConductionAPI.callEndpoint(url, 'POST', payload);

			if (endpointCall.response && !endpointCall.response.ok) {
				console.error(await endpointCall.response.json());
				return false;
			}

			if (endpointCall.errorMessage != undefined) {
				return false;
			}

			return true;
		} catch {
			return false;
		}
	}

	public static async registerInstrumentalist(username: string, email: string, firstName: string, lastName: string, password: string, confirmPassword: string, instruments: string[], accessCode: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}register/instrumentalist`;
		const payload = {
			username,
			accessCode,
			email,
			firstName,
			lastName,
			password,
			confirmPassword,
			instruments
		};

		try {
			const endpointCall = await ConductionAPI.callEndpoint(url, 'POST', payload);

			if (endpointCall.response && !endpointCall.response.ok) {
				console.error(await endpointCall.response.json());
				return false;
			}

			if (endpointCall.errorMessage != undefined) {
				return false;
			}

			return true;
		} catch {
			return false;
		}
	}

	public static async registerStudent(username: string, studentAccessCode: string, password: string, confirmPassword: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}register/student`;
		const payload = {
			username,
			studentAccessCode,
			password,
			confirmPassword,
		};

		const endpointCall = await ConductionAPI.callEndpoint(url, 'POST', payload);

		if (endpointCall.response && !endpointCall.response.ok) {
			console.error(await endpointCall.response.json());
			return false;
		}

		if (endpointCall.errorMessage != undefined) {
			return false;
		}

		return true;
	}

	/**
	 * Registers many students at once in a classroom.
	 * - Calls ConductionAPI: POST /users/register/students/bulk
	 * - Server resolves username conflicts and returns the final usernames.
	 * - Requires auth token; caller should ensure the user has ConductionAdmin
	 *   or ClassroomAdmin (and is admin of that classroom on the server side).
	 */
	public static async registerStudentsBulk(
		classroomId: string,
		students: BulkRegisterStudentItem[],
		token: string
	): Promise<
		| { success: true; results: BulkRegisterStudentResult[] }
		| { success: false; errors: string[]; status?: number; message?: string }
	> {
		const url = new URL(`register/bulk-students`, appSettings.ConductionApiBaseUrl);
		const payload = { classroomId, students };

		try {
			const resp = await fetch(url.toString(), {
				method: "POST",
				headers: ConductionAPI.getAuthHeaders(token),
				body: JSON.stringify(payload),
			});

			const contentType = resp.headers.get("content-type") ?? "";

			// ---- Success ----
			if (resp.ok) {
				if (contentType.includes("application/json")) {
					const data = (await resp.json()) as BulkRegisterStudentResult[]; // read once
					return { success: true, results: data };
				} else {
					const txt = await resp.text(); // read once (unexpected type)
					return {
						success: false,
						errors: [`Unexpected response type: ${contentType || "unknown"}`, txt],
						status: resp.status,
						message: txt,
					};
				}
			}

			// ---- Error ----
			if (contentType.includes("application/json")) {
				const body = await resp.json(); // read once
				let errors: string[] = [];
				let message: string | undefined;

				if (Array.isArray(body)) {
					errors = body.map(String);
				} else if (Array.isArray((body as any).errors)) {
					errors = (body as any).errors.map((e: any) => String(e));
					if (typeof (body as any).message === "string") message = (body as any).message;
				} else if (typeof (body as any).message === "string") {
					message = (body as any).message;
					errors = [message ?? ""];
				} else {
					message = JSON.stringify(body);
					errors = [message];
				}

				return { success: false, errors, status: resp.status, message };
			} else {
				const txt = await resp.text(); // read once
				const message = txt || `(${resp.status}) ${resp.statusText}`;
				return { success: false, errors: [message], status: resp.status, message };
			}
		} catch (err) {
			const msg = err instanceof Error ? err.message : "Unknown error";
			return { success: false, errors: [msg] };
		}
	}

	public static async registerClassroomAdmin(username: string, classroomAdminAccessCode: string, email: string, password: string, confirmPassword: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}register/classroomadmin`;
		const payload = {
			username,
			classroomAdminAccessCode,
			email,
			password,
			confirmPassword,
		};

		const endpointCall = await ConductionAPI.callEndpoint(url, 'POST', payload);

		if (endpointCall.response && !endpointCall.response.ok) {
			console.error(await endpointCall.response.json());
			return false;
		}

		if (endpointCall.errorMessage != undefined) {
			return false;
		}

		return true;
	}

	public static async registerConductionAdmin(username: string, conductionAdminAccessCode: string, email: string, password: string, confirmPassword: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}register/conductionadmin`;
		const payload = {
			username,
			conductionAdminAccessCode,
			email,
			password,
			confirmPassword,
		};

		const endpointCall = await ConductionAPI.callEndpoint(url, 'POST', payload);

		if (endpointCall.response && !endpointCall.response.ok) {
			console.error(await endpointCall.response.json());
			return false;
		}

		if (endpointCall.errorMessage != undefined) {
			return false;
		}

		return true;
	}

	public static async guestLogin(accessCode: string): Promise<{ user: User, token: string } | null> {
		const url = `${appSettings.ConductionApiBaseUrl}login/guest`;

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(accessCode),
		});

		if (response.ok) {
			const data = await response.json();
			const user = User.getGuestUser();
			const token = data.token;

			return { user, token };
		} else {
			if (response.status != 401) {
				throw new Error(`The API responded with ${response.status}: ${response.statusText}`);
			}
		}

		return null;
	}

	public static uploadProfileImage(file: File, token: string): Promise<void> {
		const url = new URL(`/upload-profile-image`, appSettings.PerformanceApiBaseUrl);

		const formData = new FormData();
		formData.append('imageFile', file);

		return fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token, { excludeContentType: true }), // Exclude Content-Type header
			body: formData
		}).then(response => {
			if (!response.ok) {
				return response.json().then(errorData => {
					throw new Error(`Error uploading profile image: ${errorData.error || response.statusText}`);
				});
			}
		});
	}

	public static async changePassword(currentPassword: string, newPassword: string, token: string): Promise<void> {
		const url = new URL(`/change-password`, appSettings.PerformanceApiBaseUrl);
		const payload = {
			currentPassword,
			newPassword
		};
		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(payload)
		});
		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(`Error changing password: ${errorData.error || response.statusText}`);
		}
	}

	public static async changeInstruments(newInstruments: string[], token: string): Promise<void> {
		const url = new URL(`/change-instruments`, appSettings.PerformanceApiBaseUrl);
		const payload = {
			newInstruments
		};
		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(payload)
		});
		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(`Error changing instruments: ${errorData.error || response.statusText}`);
		}
	}

	public static async changeUsername(newUsername: string, token: string): Promise<void> {
		const url = new URL(`/change-username`, appSettings.PerformanceApiBaseUrl);
		const payload = {
			newUsername
		};
		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(payload)
		});
		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(`Error changing username: ${errorData.error || response.statusText}`);
		}
	}

	public static async changeEmail(newEmail: string, token: string): Promise<void> {
		const url = new URL(`/change-email`, appSettings.PerformanceApiBaseUrl);
		const payload = {
			newEmail
		};
		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(payload)
		});
		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(`Error changing email: ${errorData.error || response.statusText}`);
		}
	}

	public static async sendEmailConfirmation(token: string): Promise<void> {
		const url = new URL(`/send-email-confirmation`, appSettings.PerformanceApiBaseUrl);
		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});
		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(`Error sending email confirmation: ${errorData.error || response.statusText}`);
		}
	}

	public static async confirmEmail(userId: string, token: string): Promise<void> {
		const url = new URL(`/confirm-email`, appSettings.PerformanceApiBaseUrl);
		const payload = {
			userId,
			token
		};
		const response = await fetch(url, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload)
		});
		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(`Error confirming email: ${errorData.error || response.statusText}`);
		}
	}

	public static async login(usernameOrEmail: string, password: string, rememberMe: boolean = false): Promise<{ user: User, token: string } | null> {
		const url = new URL("login", appSettings.ConductionApiBaseUrl);
		const payload = {
			usernameOrEmail,
			password,
			rememberMe
		};

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		if (response.ok) {
			// Store the JWT token and userId from the response
			const data = await response.json();
			const token = data.token;
			const user = User.fromJson(data.user);
			return { user, token }
		} else {
			if (response.status != 401) {
				throw new Error(`The API responded with ${response.status}: ${response.statusText}`);
			}
		}
		return null;
	}
}